import { toDateInputValue, toTimeInputValue } from "./dateTime";

// `Content-Disposition: attachment; filename="foo.xlsx"` (also handles
// the RFC 5987 `filename*=UTF-8''foo.xlsx` form some backends use for
// non-ASCII names). Real header value confirmed for `GET
// /admin/action-logs?isExport=true` — see docs/decisions.md #74.
export const parseFilenameFromContentDisposition = (headerValue: unknown): string | null => {
  if (typeof headerValue !== "string") return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(headerValue);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

// Saves an already-built file (the backend's own export response) as-is —
// no re-wrapping, no timestamp suffix (the backend already puts one in
// its filename, e.g. "activity_logs_03-09-2026_0627.xlsx"). Use
// `downloadFile` below instead for the handful of pages still building a
// CSV client-side from mock data (no real export endpoint to call yet).
export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);

  document.body.appendChild(link);
  link.click();

  link.remove();
  window.URL.revokeObjectURL(url);
};

interface DownloadFileOptions {
  data: BlobPart;
  fileName: string; // base name without extension
  extension?: string; // default: xlsx
  includeTimestamp?: boolean; // default: true
}

export const downloadFile = ({
  data,
  fileName,
  extension = "xlsx",
  includeTimestamp = true,
}: DownloadFileOptions) => {
  try {
    let finalFileName = fileName;

    if (includeTimestamp) {
      const now = new Date();

      // Local time, e.g. "2026-10-01_14-30-05" — matches the user's clock.
      const formattedDate = `${toDateInputValue(now)}_${toTimeInputValue(now).replace(":", "-")}-${String(now.getSeconds()).padStart(2, "0")}`;

      finalFileName = `${fileName}_${formattedDate}`;
    }

    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${finalFileName}.${extension}`);

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("File download failed:", error);
  }
};
